import React, { useEffect, useRef, useState } from 'react';

import { Box, Flex, FlexProps, Space } from '@mantine/core';
import { ChatGradient } from 'components/SmartAssistantComponents/ChatGradient';
import { ChatMessageRenderer } from 'components/SmartAssistantComponents/ChatMessageRenderer';
import { ChatStaticToolsView } from 'components/SmartAssistantComponents/ChatStaticToolsView';
import { ChatWelcomeHeader, ChatWelcomeHeaderProps } from 'components/SmartAssistantComponents/ChatWelcomeHeader';
import { PoweredByLabel } from 'components/SmartAssistantComponents/PoweredByLabel';
import { SmartAssistantChatInput } from 'components/SmartAssistantComponents/SmartAssistantChatInput';
import { AnimatePresence } from 'framer-motion';
import { ToolVisibilityOverrides } from 'hooks/use-tool-visibility';
import { CONVERSATION_VIEW_MAW } from 'shared/constants/theme';
import { ViewMode } from 'shared/enums/ViewMode';
import { ChatMessage, ForcedToolInvocation, ToolEventType } from 'shared/interfaces/ChatMessage';
import { ChatAttachmentConfiguration, SmartAssistantVisualConfig } from 'shared/interfaces/SmartAssistant';
import { ToolConfig, ToolKind } from 'shared/interfaces/tools/Tool';
import { MBox } from 'shared/motion/MBox';

interface ChatConversationViewProps extends FlexProps {
  messages: ChatMessage[];
  smartAssistantVisualConfig: SmartAssistantVisualConfig;
  smartAssistantId?: string;
  toolConfigs?: ToolConfig[];
  conversationId?: string;
  onSend?: (chatMessage: ChatMessage) => void;
  header?: React.ReactNode;
  isInputDisabled?: boolean;
  isStreamingResponse?: boolean;
  viewMode?: ViewMode;
  displayPoweredBy?: boolean;
  inputTopSection?: React.ReactNode;
  assistantBadgeTitle?: string;
  addNewMessageToConversation?: (newMessage: ChatMessage) => void;
  forceToolInvocation?: (forcedToolInvocation: ForcedToolInvocation) => void;
  welcomeHeaderProps?: Omit<ChatWelcomeHeaderProps, 'smartAssistantVisualConfig'>;
  disableAnimation?: boolean;
  disableInputZoomingWithinIframe?: boolean;
  isGuest?: boolean;
  chatAttachmentConfiguration?: ChatAttachmentConfiguration;
  visibilityOverrides?: ToolVisibilityOverrides;
  hideMargins?: boolean;
}

export function ChatConversationView({
  messages,
  smartAssistantVisualConfig,
  smartAssistantId,
  toolConfigs,
  conversationId,
  onSend,
  header,
  isInputDisabled = false,
  isStreamingResponse,
  viewMode = 'live',
  displayPoweredBy,
  inputTopSection,
  addNewMessageToConversation,
  forceToolInvocation,
  welcomeHeaderProps,
  disableAnimation,
  disableInputZoomingWithinIframe,
  isGuest = false,
  chatAttachmentConfiguration,
  visibilityOverrides,
  hideMargins = false,
  ...flexProps
}: ChatConversationViewProps) {
  const [isOverflowing, setIsOverflowing] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      const content = contentRef.current;
      const scroller = scrollerRef.current;
      if (content && scroller) setIsOverflowing(scroller.scrollHeight > scroller.clientHeight);
    };

    const resizeObserver = new ResizeObserver(checkOverflow);

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    checkOverflow();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const { conversationBackgroundColor, secondaryBackgroundColor } = smartAssistantVisualConfig;

  const handleSend = (chatMessage: ChatMessage) => onSend?.(chatMessage);

  const displayWelcomeHeader = welcomeHeaderProps && messages.length < 2;
  const hasEmailHandoff = messages.some((message) => message.toolEvent?.type === ToolEventType.EMAIL_HANDOFF);

  return (
    <Flex
      id="chat-conversation-view"
      direction="column"
      justify="space-between"
      h="100%"
      w="100%"
      bg={secondaryBackgroundColor}
      {...flexProps}>
      {!displayWelcomeHeader ? header : undefined}
      <Flex
        direction="column"
        justify="space-between"
        bg={conversationBackgroundColor}
        m={hideMargins ? 0 : 4}
        sx={{ borderRadius: '12px', flexGrow: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
        <ChatGradient smartAssistantVisualConfig={smartAssistantVisualConfig} />
        <Flex
          ref={scrollerRef}
          h="100%"
          direction={isOverflowing ? 'column-reverse' : 'column'}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            scrollBehavior: 'smooth',
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          }}>
          <AnimatePresence initial={false}>
            <Box
              ref={contentRef}
              w="100%"
              maw={CONVERSATION_VIEW_MAW}
              ml="auto"
              mr="auto"
              px="lg"
              style={{ zIndex: 1 }}>
              {displayWelcomeHeader && (
                <ChatWelcomeHeader
                  mt={50}
                  mb={20}
                  ml="auto"
                  mr="auto"
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: disableAnimation ? 0 : 0.2, ease: 'easeOut' }}
                  smartAssistantVisualConfig={smartAssistantVisualConfig}
                  {...welcomeHeaderProps}
                />
              )}

              {[...messages].map((message, index) => {
                let fallBackKey;
                if (!conversationId) {
                  fallBackKey = `${message.text.slice(0, 5)}-${message.text.slice(-5)}`;
                }
                return (
                  <MBox
                    key={`${conversationId ?? fallBackKey}-${index}`}
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      duration: disableAnimation ? 0 : 0.2,
                      ease: 'easeOut',
                      delay: message.fromUser || disableAnimation ? 0 : 0.2
                    }}>
                    <ChatMessageRenderer
                      message={message}
                      smartAssistantVisualConfig={smartAssistantVisualConfig}
                      toolConfigs={toolConfigs}
                      viewMode={viewMode}
                      forceToolInvocation={forceToolInvocation}
                    />
                  </MBox>
                );
              })}
              {smartAssistantId && toolConfigs && toolConfigs.length > 0 && (
                <ChatStaticToolsView
                  smartAssistantVisualConfig={smartAssistantVisualConfig}
                  smartAssistantId={smartAssistantId}
                  toolConfigs={toolConfigs}
                  viewMode={viewMode}
                  conversationLength={messages.length}
                  addNewMessageToConversation={addNewMessageToConversation}
                  visibilityOverrides={{
                    ...visibilityOverrides,
                    // Override lead collection tool visibility if email handoff is present
                    ...(hasEmailHandoff && { [ToolKind.COLLECT_LEAD_INFO]: false })
                  }}
                  initial={{ y: 50, opacity: 0 }}
                  animate={{
                    y: 0,
                    opacity: 1,
                    transition: {
                      duration: disableAnimation ? 0 : 0.2,
                      ease: 'easeOut',
                      delay: disableAnimation ? 0 : 1
                    }
                  }}
                  exit={{
                    x: -50,
                    opacity: 0,
                    transition: {
                      duration: disableAnimation ? 0 : 0.2,
                      ease: 'easeOut',
                      delay: 0
                    }
                  }}
                />
              )}
            </Box>
          </AnimatePresence>
        </Flex>
        <Box pb={12} maw={CONVERSATION_VIEW_MAW} mx="auto" w="100%" px="lg" style={{ zIndex: 2 }}>
          {inputTopSection}
          {inputTopSection && <Space h={5} />}
          <SmartAssistantChatInput
            onSend={handleSend}
            smartAssistantVisualConfig={smartAssistantVisualConfig}
            isSendDisabled={isStreamingResponse ?? false}
            isInputDisabled={isInputDisabled}
            disableInputZoomingWithinIframe={disableInputZoomingWithinIframe}
            smartAssistantId={smartAssistantId}
            conversationId={conversationId}
            isGuest={isGuest}
            chatAttachmentConfiguration={chatAttachmentConfiguration}
          />
          {displayPoweredBy && (
            <PoweredByLabel
              w="100%"
              direction="column"
              align="center"
              pt={12}
              smartAssistantVisualConfig={smartAssistantVisualConfig}
            />
          )}
        </Box>
      </Flex>
    </Flex>
  );
}
